// bugtracker.browser.js — vendored client SDK.
// Target: ~2kb gz, zero deps, works in any modern browser that has fetch.
// Usage:
//   import { BugTracker } from './bugtracker.browser.js';
//   BugTracker.init({ endpoint, apiKey, app, release, environment, getUser });
//   BugTracker.capture(err, { extra: {...} });

const BATCH_SIZE = 5;
const FLUSH_INTERVAL_MS = 2000;
const MAX_RETRIES = 3;
const MAX_QUEUE = 50;

let config = null;
let queue = [];
let flushTimer = null;
let installed = false;

function now() { return new Date().toISOString(); }

function getUserSafe() {
  if (!config || typeof config.getUser !== 'function') return {};
  try {
    const u = config.getUser();
    if (!u || typeof u !== 'object') return {};
    return {
      user_id: u.id != null ? String(u.id) : null,
      user_email: u.email || null,
      user_name: u.name || null,
      user_role: u.role || null,
    };
  } catch {
    return {};
  }
}

function buildEvent(input) {
  const base = {
    occurred_at: now(),
    severity: 'error',
    source: 'browser',
    url: typeof window !== 'undefined' && window.location ? window.location.href : null,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    release: config.release || null,
    environment: config.environment || null,
  };
  return { ...base, ...getUserSafe(), ...input };
}

function enqueue(ev) {
  if (!config) return;
  if (queue.length >= MAX_QUEUE) {
    queue.shift();
  }
  queue.push(ev);
  scheduleFlush();
  if (queue.length >= BATCH_SIZE) flush();
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, FLUSH_INTERVAL_MS);
}

async function flush(attempt = 0) {
  if (!config || !queue.length) return;
  const batch = queue.splice(0, BATCH_SIZE);
  try {
    const res = await fetch(config.endpoint.replace(/\/$/, '') + '/v1/events', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + config.apiKey,
      },
      body: JSON.stringify(batch),
      keepalive: true,
    });
    if (!res.ok && res.status >= 500) throw new Error('server ' + res.status);
  } catch (err) {
    if (attempt >= MAX_RETRIES) {
      if (typeof console !== 'undefined') {
        console.warn('[bugtracker] dropped batch after retries:', err && err.message);
      }
      return;
    }
    queue.unshift(...batch);
    const delay = Math.min(30_000, 500 * 2 ** attempt);
    setTimeout(() => flush(attempt + 1), delay);
  }
}

function formatError(err) {
  if (err instanceof Error) {
    return { message: err.message || String(err), stack: err.stack || null };
  }
  if (err && typeof err === 'object') {
    return { message: err.message || JSON.stringify(err), stack: err.stack || null };
  }
  return { message: String(err), stack: null };
}

function installHooks() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (e) => {
    const { message, stack } = formatError(e.error || e.message);
    enqueue(buildEvent({ message, stack }));
  });

  window.addEventListener('unhandledrejection', (e) => {
    const { message, stack } = formatError(e.reason);
    enqueue(buildEvent({ message: 'Unhandled rejection: ' + message, stack }));
  });

  window.addEventListener('pagehide', () => { try { flush(); } catch {} });
}

export const BugTracker = {
  init(opts) {
    if (!opts || !opts.endpoint || !opts.apiKey || !opts.app) {
      throw new Error('BugTracker.init requires { endpoint, apiKey, app }');
    }
    config = {
      endpoint: opts.endpoint,
      apiKey: opts.apiKey,
      app: opts.app,
      release: opts.release || null,
      environment: opts.environment || null,
      getUser: opts.getUser || null,
    };
    installHooks();
  },

  capture(err, extra = {}) {
    if (!config) return;
    const { message, stack } = formatError(err);
    enqueue(buildEvent({
      message,
      stack,
      context: extra.extra || extra.context || null,
      severity: extra.severity || 'error',
    }));
  },

  message(text, opts = {}) {
    if (!config) return;
    enqueue(buildEvent({
      message: String(text),
      stack: null,
      severity: opts.severity || 'info',
      source: 'manual',
      context: opts.extra || null,
    }));
  },

  flush() { return flush(); },
};

export default BugTracker;
